import React, { useState } from 'react';
import { UtensilsCrossed, Mail, Lock, User, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export const Auth = ({ onAuthSuccess, isDarkMode }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      if (mode === 'register') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { display_name: displayName }
          }
        });
        if (signUpError) throw signUpError;
        onAuthSuccess(data.session);
      } else {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        onAuthSuccess(data.session);
      }
    } catch (err) {
      setError(err.message || 'Ocurrió un error inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-700">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-[32px] text-white mb-6 shadow-xl shadow-primary/20 animate-bounce-slow">
            <UtensilsCrossed size={32} />
          </div>
          <h1 className="text-4xl font-black heading-serif text-stone-900 dark:text-white mb-2">Recetik</h1>
          <p className="text-stone-500 dark:text-stone-400 font-medium">Únete a la red social de sabores.</p>
        </div>

        <div className="bg-white dark:bg-stone-900 p-10 rounded-[48px] shadow-sm border border-stone-100 dark:border-stone-800 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
          
          <div className="relative z-10">
            <h2 className="text-2xl font-black heading-serif mb-8 dark:text-white">
              {mode === 'login' ? 'Bienvenido de nuevo' : 'Crea tu cuenta de Chef'}
            </h2>

            {error && (
              <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex items-center gap-3 text-rose-600 text-xs font-bold animate-in slide-in-from-top-2">
                <AlertCircle size={18} />
                <span className="leading-tight">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {mode === 'register' && (
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">Nombre Público</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                    <input 
                      data-testid="register-name-input"
                      type="text" required value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                      placeholder="Tu nombre de chef"
                      className="w-full p-4 pl-12 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none focus:ring-4 focus:ring-primary/5 transition-all font-bold dark:text-white"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">Correo Electrónico</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                  <input 
                    data-testid="auth-email-input"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="w-full p-4 pl-12 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none focus:ring-4 focus:ring-primary/5 transition-all font-bold dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-stone-400 mb-2 ml-1">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-300" size={18} />
                  <input 
                    data-testid="auth-password-input"
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full p-4 pl-12 rounded-2xl bg-stone-50 dark:bg-stone-950 border border-stone-100 dark:border-stone-800 outline-none focus:ring-4 focus:ring-primary/5 transition-all font-bold dark:text-white"
                  />
                </div>
              </div>

              <button 
                data-testid="auth-submit-btn"
                type="submit" disabled={isLoading}
                className="w-full py-5 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 rounded-[28px] font-black uppercase tracking-widest text-[10px] shadow-xl hover:bg-primary dark:hover:bg-primary dark:hover:text-white transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {isLoading ? <Loader2 className="animate-spin" size={18} /> : (
                  <> {mode === 'login' ? 'Entrar' : 'Registrarme'} <ArrowRight size={16} /> </>
                )}
              </button>
            </form>
          </div>
        </div>

        <p className="text-center mt-8 text-sm font-medium text-stone-500 dark:text-stone-400">
          {mode === 'login' ? '¿No tienes cuenta?' : '¿Ya eres chef?'}
          <button 
            data-testid="toggle-auth-mode"
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="ml-2 text-primary font-black hover:underline"
          >
            {mode === 'login' ? 'Únete aquí' : 'Inicia sesión'}
          </button>
        </p>
      </div>
    </div>
  );
};
